import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { filter, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Agreement, Owner, Property, TeamState, TeamStatus } from '../../models/statements.models';
import { StatementsFacade } from '../../services/statements.facade';

enum Step {
  Existing = 'existing',
  New = 'new',
}

@Component({
  standalone: false,
  selector: 'hospitable-statements-setup-flow',
  templateUrl: './statements-setup-flow.component.html',
  styleUrls: ['./statements-setup-flow.component.scss'],
})
export class StatementsSetupFlowComponent implements OnDestroy, OnInit {
  @ViewChild('modalHeader') modalHeader: ElementRef;

  private destroyed$ = new Subject<void>();

  stages = ['first_owner', 'properties_match', 'commission', 'summary'];
  currentStage = this.stages[0];

  owner = this.statementsService.newOwner();
  creatingOwner = false;
  addingProperties = false;
  private teamState$ = this.statementsService.teamState$.pipe(
    filter((state) => Boolean(state.data)),
    take(1),
    shareReplay(1)
  );

  public ownersState$ = this.statementsService.ownersState$.pipe(
    tap((state) => {
      if (!state.loading && !state.error && state.data?.length) {
        this.ownerStep$.next(Step.Existing);
      }
    })
  );
  public propertiesState$ = this.statementsService.propertiesState$;
  public commissionState$ = this.statementsService.commissionState$.pipe(
    tap((state) => {
      if (!state.loading && !state.error && state.data?.length) {
        this.commissionStep$.next(Step.Existing);
      }
    })
  );
  newCommission: Agreement;
  creatingCommission: boolean;
  public ownerStep$ = new BehaviorSubject<Step>(Step.New);
  public commissionStep$ = new BehaviorSubject<Step>(Step.New);
  public StepEnum = Step;
  public selectedProperties: number[] = [];

  constructor(
    private statementsService: StatementsFacade,
    private notificationService: ToastNotificationsService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.statementsService.getTeam();
    this.statementsService.getOwners();
    this.statementsService.getCommissionAgreements();
    this.statementsService.getProperties();
  }

  ngOnInit() {
    this.validateAndEnforceOnboarding();
  }

  selectOwner(owner: Owner) {
    if (this.ownerStep$.value === 'existing') {
      this.selectOwnerSuccess(owner);
      return;
    }

    this.creatingOwner = true;

    this.statementsService
      .createOwner(owner)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (owner) => {
          this.selectOwnerSuccess(owner);
          this.updateOnboardingStatus(TeamStatus.AssignProperties);
        },
        error: (error) => {
          this.creatingOwner = false;
          this.notificationService.open(
            error?.message ?? 'An error occurred while saving the owner.',
            'OK',
            NotificationType.Error
          );
        },
        complete: () => {
          this.creatingOwner = false;
        },
      });
  }

  private selectOwnerSuccess(owner: Owner) {
    this.owner.id = owner.id;
    this.nextStage();
  }

  public setOwnerStep(step: Step) {
    if (step === Step.New) {
      this.owner = this.statementsService.newOwner();
    }
    this.ownerStep$.next(step);
  }

  public setCommissionStep(step: Step) {
    this.commissionStep$.next(step);
  }

  toggleProperty(event: MatCheckboxChange, property: Property) {
    if (event.checked) {
      this.selectedProperties = [...this.selectedProperties, property.id];
      this.owner.properties = [...this.owner.properties, property];
    } else {
      this.selectedProperties = this.selectedProperties.filter((id) => id !== property.id);
      this.owner.properties = this.owner.properties.filter((p) => p.id !== property.id);
    }
  }

  updateOwnerWithProperties(owner: Owner) {
    this.addingProperties = true;
    this.statementsService.updateOwner(owner, true).subscribe({
      next: () => {
        this.addingProperties = false;

        this.updateOnboardingStatus(TeamStatus.CreateCommissionRate);

        this.nextStage();
      },
      error: (error) => {
        this.addingProperties = false;
        this.notificationService.open(
          error?.message ?? "An error occurred while updating the owner's properties.",
          'OK',
          NotificationType.Error
        );
      },
    });
  }

  selectCommission() {
    this.creatingCommission = true;
    if (this.commissionStep$.value === 'existing') {
      this.statementsService
        .assignPropertiesToCommissionRate({
          commissionRateId: this.newCommission.id,
          properties: this.selectedProperties,
        })
        .subscribe({
          next: () => {
            this.creatingCommission = false;
            this.nextStage();
          },
          error: (error) => {
            this.creatingCommission = false;
            this.notificationService.open(
              error?.message ?? 'An error occurred while saving commission rate properties.',
              'OK',
              NotificationType.Error
            );
          },
        });
      return;
    }

    this.statementsService.createCommissionAgreement(this.newCommission, this.selectedProperties).subscribe({
      next: () => {
        this.creatingCommission = false;
        this.updateOnboardingStatus(TeamStatus.Finished);
        this.nextStage();
      },
      error: (error) => {
        this.creatingCommission = false;
        this.notificationService.open(
          error?.message ?? 'An error occurred creating commission agreement.',
          'OK',
          NotificationType.Error
        );
      },
    });
  }

  nextStage() {
    const currentIndex = this.stages.indexOf(this.currentStage);
    this.currentStage = this.stages[currentIndex + 1];

    this.modalHeader.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  onFinishFlow() {
    this.navigateToParent();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private updateOnboardingStatus(status: TeamStatus) {
    this.teamState$.subscribe((teamState) => {
      if (teamState.data.status === TeamStatus.Finished) {
        return;
      }
      this.statementsService.updateOnboardingStatus(status);
    });
  }

  private validateAndEnforceOnboarding() {
    this.teamState$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((teamState) => {
          if (teamState.data.status === TeamStatus.Finished) {
            return this.propertiesState$;
          }

          this.restartOnboarding(teamState.data);
          return of(null);
        })
      )
      .subscribe((propertiesState) => {
        // if onboarding completed, don't allow user to enter setup when no more properties are available
        const availableProperties = propertiesState?.data?.filter((property) => property.owners.length === 0);
        if (!propertiesState || availableProperties?.length) {
          return;
        }

        this.navigateToParent();
        this.notificationService.open('All properties are already assigned to an owner.', 'OK', NotificationType.Error);
      });
  }

  private navigateToParent() {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  private restartOnboarding(teamState: TeamState['data']) {
    // set the right stage
    if (teamState.status === TeamStatus.AssignProperties) {
      this.currentStage = 'properties_match';
    }

    if (teamState.status === TeamStatus.CreateCommissionRate) {
      this.currentStage = 'commission';
    }

    // set the owner
    if (this.stages.indexOf(this.currentStage) > 0) {
      this.owner = teamState.owner;
    }
    // set the selected properties
    if (this.stages.indexOf(this.currentStage) > 1) {
      this.selectedProperties = teamState.owner.properties.map((p) => p.id);
    }
  }
}
